/**
 * The Telegram outbox (twin.notifications, migration 0015) and the bot's long poll
 * (specs/c2-kontratlar.md 2.4.7, 4.10, K9). Services write rows; only the bot delivers
 * them. text never carries customer text: the bot reads an approval card through
 * GET /api/v1/approvals/{id}.
 */
import { z } from 'zod';
import { boundedText, countSchema, idStringSchema, isoDateTimeSchema } from './ids.js';
export const NOTIFICATION_KINDS = [
    /** The bot reads the card from the API and draws it; text is only a heading. */
    'approval_card',
    'executor_result',
    'report_daily',
    'report_weekly',
    'agent',
    'budget',
    'stage_health',
    'system',
    'unknown_questions',
    /** The Sekreter's list of what waits for Sercan (v0.1.24). */
    'sekreter',
    /** A question for Sercan (twin.requests): the bot reads GET /requests/{id} and draws the options as buttons (v0.1.77). */
    'request_card',
    /** v0.1.248: an approval card left pending; the bot edits its Telegram messages (editMessageIds), no buttons. */
    'approval_closed',
    /** v0.1.248: a visitor chatted with the site assistant; the bot reads GET /site-chats/{siteChatId}?from=siteTurnId. */
    'site_chat',
    /** v0.1.255: the Monday digest (plain text); the week's info lines are folded into it. */
    'digest_weekly',
];
export const notificationKindSchema = z.enum(NOTIFICATION_KINDS);
export const notificationLevelSchema = z.enum(['info', 'warning', 'critical']);
/** twin.notifications.text_tr is 1..3500 characters, counted like Postgres length() (Telegram allows 4096). */
export const NOTIFICATION_TEXT_MAX = 3500;
/** After this many failed deliveries a row becomes dropped. */
export const NOTIFICATION_MAX_ATTEMPTS = 5;
/** Longest long poll wait in seconds; the bot's delivery client times out after wait + 5 s. */
export const NOTIFICATION_MAX_WAIT_SECONDS = 25;
export const NOTIFICATION_CLIENT_TIMEOUT_MARGIN_MS = 5000;
/** twin.notifications.last_error is at most 300 characters. */
export const NOTIFICATION_ERROR_MAX = 300;
/** twin.notifications.dedupe_key is at most 200 characters. */
export const NOTIFICATION_DEDUPE_KEY_MAX = 200;
export const notificationItemSchema = z.object({
    id: idStringSchema,
    kind: notificationKindSchema,
    level: notificationLevelSchema,
    text: boundedText(1, NOTIFICATION_TEXT_MAX),
    approvalId: idStringSchema.nullable(),
    reportId: idStringSchema.nullable(),
    /** request_card only (v0.1.77); absent on answers from an older api. */
    requestId: idStringSchema.nullable().optional(),
    /** approval_closed only (v0.1.248): the Telegram message ids that showed the card. */
    editMessageIds: z.array(z.number().int().positive()).max(50).optional(),
    /** site_chat only (v0.1.248). */
    siteChatId: z.uuid().optional(),
    siteTurnId: idStringSchema.optional(),
    createdAt: isoDateTimeSchema,
});
/** GET /api/v1/notifications?status=pending&limit=N&wait=S. */
export const notificationListSchema = z.object({
    items: z.array(notificationItemSchema),
    total: countSchema,
});
export const notificationListQuerySchema = z.strictObject({
    status: z.literal('pending').default('pending'),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    wait: z.coerce.number().int().min(0).max(NOTIFICATION_MAX_WAIT_SECONDS).default(0),
});
/** POST /api/v1/notifications/{id}/delivery. */
export const deliveryRequestSchema = z.strictObject({
    status: z.enum(['sent', 'failed']),
    telegramMessageId: z.number().int().positive().optional(),
    error: z.string().min(1).max(NOTIFICATION_ERROR_MAX).optional(),
});
export const deliveryResponseSchema = z.object({
    id: idStringSchema,
    status: z.enum(['sent', 'failed', 'pending']),
});
/** Dedupe keys: one notification per event (INSERT ... ON CONFLICT DO NOTHING). */
export const dedupeKeys = Object.freeze({
    approvalCard: (approvalId) => `approval:${approvalId}`,
    execution: (executionId, status) => `exec:${executionId}:${status}`,
    stage: (pipeline, stage, health, day) => `stage:${pipeline}/${stage}:${health}:${day}`,
    report: (reportId) => `report:${reportId}`,
});
//# sourceMappingURL=notifications.js.map